import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getOrderById } from "../services/Order/OrderService";
import { getVNPayPaymentStatus } from "../services/Checkout/CheckoutService";
import {
  clearPendingVNPayOrder,
  getPendingVNPayOrder,
} from "../services/Checkout/PendingOrderStorage";
import { removeFromCart as removeCartItem } from "../services/Cart/CartServices";
import useCheckoutProductStore from "../stores/CheckoutProduct/useCheckoutProductStore";
import useCartStore from "../stores/Cart/useCartStore";

/**
 * Custom hook for processing payment results and order completion
 * Handles both URL-based payments (VNPay/PayPal) and state-based payments (COD)
 */
const usePaymentProcessor = () => {
  const location = useLocation();
  const {
    completeOrder,
    clearCheckoutItems,
    getCheckoutItemKeys,
  } = useCheckoutProductStore();
  const { showCart } = useCartStore();

  const [finalOrderData, setFinalOrderData] = useState(null);
  const [isProcessingComplete, setIsProcessingComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [verifiedStatus, setVerifiedStatus] = useState(null);
  const [verifiedPaymentData, setVerifiedPaymentData] = useState(null);

  // Extract payment data from URL or navigation state
  const extractPaymentData = () => {
    // Get data from URL params (VNPay/PayPal)
    const query = new URLSearchParams(location.search);
    const urlData = {
      status: query.get("status"),
      orderId: query.get("order_id"),
      transactionNo: query.get("transaction_no"),
      amount: query.get("amount"),
      paymentDate: query.get("payment_date"),
    };

    // Get data from navigation state (COD)
    const stateData = location.state;
    const orderData = stateData?.orderData;
    const paymentInfo = stateData?.paymentInfo;
    const pendingOrder = getPendingVNPayOrder();
    
    // A valid pending order is identifier fallback only; it never confirms success.
    return {
      orderId:
        urlData.orderId ||
        paymentInfo?.order_id ||
        orderData?.orderId ||
        verifiedPaymentData?.order_id ||
        pendingOrder?.order_id,
      status: urlData.status || paymentInfo?.status,
      transactionNo:
        urlData.transactionNo ||
        paymentInfo?.transaction_no ||
        verifiedPaymentData?.transaction_ref ||
        pendingOrder?.transaction_ref ||
        `COD-${urlData.orderId || orderData?.orderId}`,
      amount: urlData.amount || paymentInfo?.amount || orderData?.total || verifiedPaymentData?.amount || pendingOrder?.amount,
      paymentDate: urlData.paymentDate || paymentInfo?.payment_date || orderData?.paymentInfo?.paymentDate,
      paymentMethod: paymentInfo?.payment_method || verifiedPaymentData?.payment_method || pendingOrder?.payment_method || "vnpay",
      lookupToken: pendingOrder?.lookup_token,
      pendingOrder,
      orderData,
      stateData,
    };
  };

  const paymentData = extractPaymentData();

  // Determine payment status
  const getPaymentStatus = () => {
    const { status, paymentMethod } = paymentData;
    const effectiveStatus =
      paymentMethod === "vnpay" ? verifiedStatus : status;
    
    return {
      isPaymentSuccess: effectiveStatus === "success" || effectiveStatus === "00",
      isPaymentFailed:
        effectiveStatus === "failed" ||
        effectiveStatus === "error" ||
        (effectiveStatus && effectiveStatus !== "pending" && effectiveStatus !== "success" && effectiveStatus !== "00"),
      isCODOrder: paymentMethod === "cod",
    };
  };

  const paymentStatus = getPaymentStatus();

  // Transform API response to standardized format
  const transformOrderData = (apiResponse, paymentData) => {
    const { orderId, transactionNo, amount, paymentDate } = paymentData;
    const { isPaymentSuccess, isPaymentFailed } = paymentStatus;

    return {
      orderId: apiResponse.id || apiResponse.number || orderId,
      items: apiResponse.line_items?.map((item) => ({
        id: item.id,
        name: item.name,
        quantity: item.quantity,
        price: item.price,
        total: parseFloat(item.total),
        image: item.image?.src || null,
        sku: item.sku,
      })) || [],
      total: parseFloat(apiResponse.total),
      customerInfo: {
        name: `${apiResponse.billing?.first_name || ''} ${apiResponse.billing?.last_name || ''}`.trim().toUpperCase(),
        email: apiResponse.billing?.email || '',
        phone: apiResponse.billing?.phone || '',
        shippingAddress: `${apiResponse.shipping?.address_1 || ''} ${apiResponse.shipping?.address_2 || ''}, ${apiResponse.shipping?.city || ''}, ${apiResponse.shipping?.country || ''}`.trim(),
        billingAddress: `${apiResponse.billing?.address_1 || ''} ${apiResponse.billing?.address_2 || ''}, ${apiResponse.billing?.city || ''}, ${apiResponse.billing?.country || ''}`.trim(),
      },
      paymentInfo: {
        orderId: apiResponse.id || apiResponse.number || orderId,
        transactionNo: transactionNo || apiResponse.transaction_id || "N/A",
        amount: amount || apiResponse.total,
        paymentDate: paymentDate || apiResponse.date_created || new Date().toISOString(),
        status: isPaymentSuccess ? "completed" : isPaymentFailed ? "failed" : apiResponse.status || "processing",
        paymentMethod: apiResponse.payment_method || paymentData.paymentMethod || "N/A",
        currency: apiResponse.currency || "VND",
        currencySymbol: apiResponse.currency_symbol || "₫",
      },
      orderStatus: apiResponse.status,
      dateCreated: apiResponse.date_created,
      orderKey: apiResponse.order_key,
    };
  };

  // Handle successful payment processing
  const handleSuccessfulPayment = async (orderData) => {
    try {

      const checkoutItemKeys =
        paymentData.pendingOrder?.cart_item_keys || getCheckoutItemKeys();

      // Complete the order
      const completedOrder = completeOrder({
        orderId: paymentData.orderId || orderData.orderId,
        transactionNo: paymentData.transactionNo || "N/A",
        amount: paymentData.amount || orderData.paymentInfo?.total,
        paymentDate: paymentData.paymentDate || new Date().toISOString(),
        status: "completed",
      });


      // Remove items from cart
      if (checkoutItemKeys?.length > 0) {
        const cartStore = useCartStore.getState();

        if (cartStore.isGuestMode) {
          cartStore._clearGuestCartItems(checkoutItemKeys);
        } else {
          for (const cartItemKey of checkoutItemKeys) {
            try {
              await removeCartItem({ cart_item_key: cartItemKey });
            } catch (error) {
              // Silent error handling for production
            }
          }

          await showCart();
        }
      }

      // Clear checkout items
      clearCheckoutItems();
      sessionStorage.removeItem("currentOrderData");
      if (paymentData.paymentMethod === "vnpay") {
        clearPendingVNPayOrder();
      }

      return completedOrder;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Fetch order data from API
  const fetchOrderData = async () => {
    if (!paymentData.orderId) {
      setError("No order ID found");
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      if (paymentData.paymentMethod === "vnpay") {
        if (!paymentData.lookupToken) {
          throw new Error("Unable to verify this VNPay payment.");
        }

        const response = await getVNPayPaymentStatus(
          paymentData.orderId,
          paymentData.lookupToken
        );
        setVerifiedStatus(response.status);
        setVerifiedPaymentData(response);

        const storedOrderData = sessionStorage.getItem("currentOrderData");
        const localOrderData = storedOrderData ? JSON.parse(storedOrderData) : null;
        const paymentInfo = {
          ...(localOrderData?.paymentInfo || {}),
          orderId: response.order_id,
          amount: response.amount,
          currency: response.currency,
          paymentMethod: response.payment_method,
          status: response.status,
          transactionNo: response.transaction_ref,
        };
        const displayOrder = localOrderData
          ? { ...localOrderData, orderId: response.order_id, paymentInfo }
          : { orderId: response.order_id, items: [], paymentInfo };
        setFinalOrderData(displayOrder);

        if (response.status === "success" && !isProcessingComplete) {
          setIsProcessingComplete(true);
          await handleSuccessfulPayment(displayOrder);
        } else if (response.status === "failed") {
          clearPendingVNPayOrder();
        }
        return;
      }

      const response = await getOrderById(paymentData.orderId);

      if (response) {
        const transformedOrderData = transformOrderData(response, paymentData);
        setFinalOrderData(transformedOrderData);

        // Process successful payment
        if ((paymentStatus.isPaymentSuccess || paymentStatus.isCODOrder) && !isProcessingComplete) {
          await handleSuccessfulPayment(transformedOrderData);
          setIsProcessingComplete(true);
        }
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Main effect
  useEffect(() => {
    // Handle COD orders from state
    if (paymentData.orderData && paymentStatus.isCODOrder) {
      setFinalOrderData(paymentData.orderData);
      setIsLoading(false);

      if (!isProcessingComplete) {
        handleSuccessfulPayment(paymentData.orderData);
        setIsProcessingComplete(true);
      }
      return;
    }

    if (paymentData.paymentMethod === "vnpay" && isProcessingComplete) {
      return;
    }

    // Fetch order data for other payment methods
    fetchOrderData();
  }, [paymentData.orderId, paymentStatus.isPaymentSuccess, isProcessingComplete]);

  return {
    finalOrderData,
    paymentStatus,
    paymentData,
    isLoading,
    error,
    isProcessingComplete,
  };
};

export default usePaymentProcessor;
